import { ChangeEvent, KeyboardEvent, RefObject, useEffect, useMemo } from 'react';

import { Menu as HeadlessMenu } from '@headlessui/react';
import styled from 'styled-components';

import { AudioRecorderButton } from '@components/audio-recorder-button';
import { Button } from '@components/button';
import { FileIconButton } from '@components/file-icon-button';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconAiChatMode } from '@components/icons/ai-chat-mode';
import { IconArrowUp } from '@components/icons/arrow-up';
import { IconCheckboxChecked } from '@components/icons/checkbox-checked';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconDropdown } from '@components/icons/dropdown';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useTranscribeAudio } from 'api/ai-builder/hooks';
import { AIBuilderMode } from 'api/ai-builder/requests';
import { actionLabels } from 'ui/actions/action-label';

import { FileTag } from './file-tag';

import type { JourneyNode } from '../../node-types/types';
import { type PendingFile } from '../hooks';

type InputBoxProps = {
  value: string;
  isDisabled?: boolean;
  isGenerating?: boolean;
  placeholder?: string;
  textAreaRef: RefObject<HTMLTextAreaElement>;
  inputRowRef: RefObject<HTMLDivElement>;
  buttonsRowRef: RefObject<HTMLDivElement>;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onInterrupt?: () => void;
  onResize: () => void;
  files: PendingFile[];
  onFileSelected: (file: File) => void;
  onFileRemove: (fileId: string) => void;
  errorText?: string;
  onClearError?: () => void;
  selectedNode?: JourneyNode;
  onTranscriptionComplete: (text: string) => void;
  onTranscriptionError: (error: string) => void;
  mode: AIBuilderMode;
  onModeChange: (mode: AIBuilderMode) => void;
};

export const InputBox = ({
  value,
  placeholder,
  textAreaRef,
  inputRowRef,
  buttonsRowRef,
  onChange,
  onSubmit,
  onResize,
  files,
  onFileSelected,
  onFileRemove,
  isDisabled = false,
  isGenerating = false,
  onInterrupt,
  errorText,
  onClearError,
  selectedNode,
  onTranscriptionComplete,
  onTranscriptionError,
  mode,
  onModeChange,
}: InputBoxProps) => {
  const { t } = useScopedTranslate(locale);
  const transcriptionMutation = useTranscribeAudio();

  useEffect(() => {
    requestAnimationFrame(onResize);
  }, [files, errorText, selectedNode, onResize]);

  const selectedActionText = useMemo(() => {
    if (!selectedNode) {
      return;
    }

    const label = actionLabels[selectedNode.type];
    return t('selectedAction', { action: label });
  }, [selectedNode, t]);

  const handleSubmitOrInterrupt = (focusAfter = false) => {
    if (isGenerating) {
      onInterrupt?.();
      return;
    }

    const trimmedValue = value.trim();
    if (trimmedValue && !isDisabled) {
      onSubmit(trimmedValue);
      if (focusAfter) {
        requestAnimationFrame(() => {
          return textAreaRef.current?.focus();
        });
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitOrInterrupt(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    requestAnimationFrame(onResize);
    onClearError?.();
  };

  const handleSubmitClick = () => {
    handleSubmitOrInterrupt(true);
  };

  return (
    <InputRow ref={inputRowRef} onKeyDown={handleKeyDown}>
      <InputTextArea
        ref={textAreaRef}
        value={value}
        onChange={handleChange}
        placeholder={placeholder || t('askAnything')}
        rows={1}
      />
      <Stack space={2} ref={buttonsRowRef}>
        {selectedActionText && (
          <SelectedActionContainer>
            <Text font="regular12" color="textHelper" text={selectedActionText} />
          </SelectedActionContainer>
        )}
        {(files.length > 0 || errorText) && (
          <Stack space={2} style={{ width: '100%' }}>
            {errorText && <Text color="elementRed100" font="regular12" text={errorText} />}
            {files.map(({ id, file }) => {
              return <FileTag key={id} name={file.name} onRemove={() => onFileRemove(id)} />;
            })}
          </Stack>
        )}
        <Stack orientation="horizontal" justify="space-between">
          <Menu
            placement="top-start"
            buttonProps={{
              iconLeft:
                mode === 'agent' ? (
                  <IconAiAgent variant="gradient" />
                ) : (
                  <IconAiChatMode variant="gradient" />
                ),
              iconRight: <IconDropdown />,
              variant: 'secondary',
              text: mode === 'agent' ? t('agentLabel') : t('planLabel'),
            }}
            width={pxToRem(320)}
          >
            <>
              <HeadlessMenu.Item>
                {({ active }) => (
                  <ModeOptionContainer data-selected={active} onClick={() => onModeChange('agent')}>
                    <Stack
                      orientation="horizontal"
                      space={2}
                      align="center"
                      justify="space-between"
                    >
                      <Stack orientation="horizontal" space={2} align="center">
                        <IconAiAgent
                          variant={mode === 'agent' ? 'solid' : 'gradient'}
                          color={mode === 'agent' ? 'elementBlue100' : 'inherit'}
                        />
                        <Text
                          font="semibold14"
                          color={mode === 'agent' ? 'elementBlue100' : 'textPrimary'}
                          text={t('agentLabel')}
                        />
                      </Stack>
                      {mode === 'agent' && <IconCheckmark color="buttonIconPositive" />}
                    </Stack>
                    <Text font="regular12" color="textSecondary" text={t('agentHelpText')} />
                  </ModeOptionContainer>
                )}
              </HeadlessMenu.Item>
              <Menu.Separator />
              <HeadlessMenu.Item>
                {({ active }) => (
                  <ModeOptionContainer data-selected={active} onClick={() => onModeChange('chat')}>
                    <Stack
                      orientation="horizontal"
                      space={2}
                      align="center"
                      justify="space-between"
                    >
                      <Stack orientation="horizontal" space={2} align="center">
                        <IconAiChatMode
                          variant={mode === 'chat' ? 'solid' : 'gradient'}
                          color={mode === 'chat' ? 'elementBlue100' : 'textPrimary'}
                        />
                        <Text
                          font="semibold14"
                          color={mode === 'chat' ? 'elementBlue100' : 'textPrimary'}
                          text={t('planLabel')}
                        />
                      </Stack>
                      {mode === 'chat' && <IconCheckmark color="buttonIconPositive" />}
                    </Stack>
                    <Text font="regular12" color="textSecondary" text={t('planHelpText')} />
                  </ModeOptionContainer>
                )}
              </HeadlessMenu.Item>
            </>
          </Menu>
          <Stack orientation="horizontal" space={2} justify="flex-end" align="center">
            <AudioRecorderButton
              onTranscriptionComplete={onTranscriptionComplete}
              onError={onTranscriptionError}
              transcriptionMutation={transcriptionMutation}
              isDisabled={isDisabled}
            />
            <FileIconButton
              key={files
                .map(({ id }) => {
                  return id;
                })
                .join('|')}
              fileType="image"
              onFileSelected={onFileSelected}
            />
            <Button
              variant="primary-blue"
              iconRight={
                isGenerating ? <IconCheckboxChecked size={16} color="white100" /> : <IconArrowUp />
              }
              onClick={handleSubmitClick}
            />
          </Stack>
        </Stack>
      </Stack>
    </InputRow>
  );
};

const InputRow = styled(Stack).attrs({
  space: 2,
})({
  minHeight: pxToRem(56),
  borderRadius: pxToRem(8),
  border: `1px solid ${theme.color.elementBlue100}`,
  padding: pxToRem(8),
  paddingLeft: pxToRem(16),
  overflow: 'hidden',
  background: theme.color.backgroundLightDark,
  position: 'absolute',
  left: pxToRem(16),
  right: pxToRem(16),
  bottom: pxToRem(16),
  zIndex: 1,
});

const SelectedActionContainer = styled.div({
  width: '100%',
  minWidth: 0,
});

const ModeOptionContainer = styled(Stack).attrs({ space: 1 })({
  padding: pxToRem(8),
  borderRadius: pxToRem(4),
  cursor: 'pointer',

  '&[data-selected="true"]': {
    backgroundColor: theme.color.uiTrans5,
  },
});

const InputTextArea = styled.textarea({
  display: 'block',
  width: '100%',
  minHeight: pxToRem(32),
  font: theme.font.regular16,
  lineHeight: 1.4,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: theme.color.textPrimary,
  resize: 'none',
  padding: `${pxToRem(8)} ${pxToRem(8)} 0 0`,
  '&::placeholder': {
    color: theme.color.textHelper,
  },
  scrollbarColor: `${theme.color.uiTrans10} transparent`,
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    width: pxToRem(8),
    height: pxToRem(8),
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.color.uiTrans10,
    borderRadius: pxToRem(8),
  },
});

const locale = {
  en: {
    askAnything: 'Ask anything',
    selectedAction: 'Selected action: {action}',
    agentLabel: 'Agent',
    planLabel: 'Plan',
    agentHelpText:
      'Fast mode for building workflows. Agent immediately adds or updates steps to complete your request.',
    planHelpText:
      'Review-first mode. Chat drafts a proposed workflow that you can edit and approve before anything changes.',
  },
};
